import { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
  Box, Stack, Typography, Skeleton, Tooltip,
  ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SEV_COLOR, TYPE_COLOR, SEV_ORDER, scoreColor } from "../constants";

const ts = (v) => {
  const raw = v?.$date ?? v;
  return raw ? dayjs(raw).format("MM-DD HH:mm") : "-";
};

const worstSeverity = (items) =>
  items.reduce((best, a) => {
    const order = SEV_ORDER[a.severity_label] ?? 99;
    return order < (SEV_ORDER[best] ?? 99) ? a.severity_label : best;
  }, "info");

const uniqueTypes = (items) => {
  const set = new Set();
  items.forEach(a => (a.anomaly_types ?? []).forEach(t => set.add(t)));
  return Array.from(set);
};

// Se un filtro tipo è attivo, mostra solo i tag selezionati anche se l'anomalia
// ne aggrega altri — altrimenti mostra tutti i tag dell'anomalia.
const visibleTypes = (types, filterTypes) =>
  filterTypes?.length > 0 ? types.filter(t => filterTypes.includes(t)) : types;

const dstCount = (anomaly) =>
  anomaly.graph?.total_peer_count ??
  new Set((anomaly.graph?.edges ?? []).map(e => e.dst).filter(Boolean)).size;

// Numero totale di destinazioni distinte su tutte le anomalie di un host
const totalDstCount = (items) => {
  const set = new Set();
  items.forEach(a => (a.graph?.edges ?? []).forEach(e => e.dst && set.add(e.dst)));
  if (set.size > 0) return set.size;
  return items.reduce((s, a) => s + dstCount(a), 0);
};

// Timestamp più recente tra le anomalie di un host
const latestSeen = (items) =>
  items.reduce((latest, a) => {
    const t = a.last_seen?.$date ?? a.last_seen ?? a.first_seen?.$date ?? a.first_seen;
    if (!t) return latest;
    return !latest || dayjs(t).isAfter(dayjs(latest)) ? t : latest;
  }, null);

// Timestamp meno recente (inizio dell'attività) tra le anomalie di un host
const earliestSeen = (items) =>
  items.reduce((earliest, a) => {
    const t = a.first_seen?.$date ?? a.first_seen;
    if (!t) return earliest;
    return !earliest || dayjs(t).isBefore(dayjs(earliest)) ? t : earliest;
  }, null);

// ── Riga unica per host: unisce tutte le anomalie di un IP ───────────────────
// Un click apre il pannello destro (drawer) sull'anomalia con score più alto;
// se l'host ne ha più di una, il drawer offre la navigazione tra le altre.
const HostRow = ({ ip, items, onOpenDrawer, filterTypes }) => {
  const worst = worstSeverity(items);
  const sevColor = SEV_COLOR[worst] ?? "#888888";
  const types = visibleTypes(uniqueTypes(items), filterTypes);
  const top = items[0];
  const destinations = totalDstCount(items);
  const lastSeen = latestSeen(items);
  const firstSeen = earliestSeen(items);
  const openCount = items.filter(a => a.status === "open").length;
  const occurrences = items.reduce((s, a) => s + (a.occurrence_count ?? 0), 0);
  const topScoreColor = scoreColor(top?.peak_score);

  return (
    <Box
      onClick={() => onOpenDrawer(top)}
      sx={{
        display: "flex", alignItems: "center", px: 1.5, py: 1,
        cursor: "pointer", borderRadius: 1.5,
        bgcolor: "#1e1e1e", border: "1px solid #2e2e2e", mb: 0.8,
        "&:hover": { borderColor: "#3a3a3a", bgcolor: "#242424" },
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: sevColor, mr: 1.2, flexShrink: 0 }} />

      <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: "#459095", mr: 1.5, flexShrink: 0 }}>
        {ip}
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={0.4} sx={{ flex: 1, minWidth: 0 }}>
        {types.slice(0, 3).map(t => {
          const c = TYPE_COLOR[t] ?? "#aaaaaa";
          return (
            <Box key={t} sx={{ px: 0.6, py: 0.1, bgcolor: `${c}22`, border: `1px solid ${c}70`, borderRadius: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: c }}>{t}</Typography>
            </Box>
          );
        })}
        {types.length > 3 && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", alignSelf: "center" }}>+{types.length - 3}</Typography>
        )}
      </Stack>

      <Box sx={{ px: 0.8, py: 0.2, ml: 1, bgcolor: "#2e2e2e", borderRadius: 0.8, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {items.length} {items.length === 1 ? "anomaly" : "anomalies"}
        </Typography>
      </Box>

      <Tooltip arrow placement="top" title={openCount === items.length ? "All anomalies are still open" : openCount === 0 ? "All anomalies are closed" : `${openCount} open, ${items.length - openCount} closed`}>
        <Box sx={{
          px: 0.7, py: 0.1, ml: 1, flexShrink: 0,
          bgcolor: openCount > 0 ? "#45909525" : "#55555525",
          border: `1px solid ${openCount > 0 ? "#459095" : "#555"}`,
          borderRadius: 0.5,
        }}>
          <Typography sx={{ fontSize: 11, color: openCount > 0 ? "#459095" : "#888", fontWeight: 600 }}>
            {openCount === 0 ? "CLOSED" : openCount === items.length ? "OPEN" : `${openCount} OPEN`}
          </Typography>
        </Box>
      </Tooltip>

      {destinations > 0 && (
        <Box sx={{
          px: 0.7, py: 0.1, ml: 1,
          bgcolor: "#2a2a2a", border: "1px solid #444",
          borderRadius: 0.5, flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: 11, color: "#7eadbb", fontFamily: "monospace" }}>
            {destinations} dst
          </Typography>
        </Box>
      )}

      {occurrences > 0 && (
        <Tooltip arrow placement="top" title="Total occurrences recorded across these anomalies">
          <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: "monospace", ml: 1, flexShrink: 0 }}>
            ×{occurrences.toLocaleString()}
          </Typography>
        </Tooltip>
      )}

      <Box sx={{
        px: 0.7, py: 0.1, ml: 1,
        bgcolor: `${sevColor}22`, border: `1px solid ${sevColor}`,
        borderRadius: 0.5, flexShrink: 0,
      }}>
        <Typography sx={{ fontSize: 11, color: sevColor, fontWeight: 700 }}>
          {worst?.toUpperCase() ?? "-"}
        </Typography>
      </Box>

      <Tooltip arrow placement="top" title={`Peak score ${typeof top?.peak_score === "number" ? top.peak_score.toFixed(3) : "-"} · current ${typeof top?.current_score === "number" ? top.current_score.toFixed(3) : "-"}`}>
        <Typography sx={{
          fontSize: 12, fontFamily: "monospace", color: topScoreColor,
          ml: 1, flexShrink: 0, minWidth: 40, textAlign: "right",
        }}>
          {typeof top?.peak_score === "number" ? top.peak_score.toFixed(3) : "-"}
        </Typography>
      </Tooltip>

      <Tooltip arrow placement="top" title={`First seen ${ts(firstSeen)} · last seen ${ts(lastSeen)}`}>
        <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 1, flexShrink: 0 }}>
          {ts(lastSeen)}
        </Typography>
      </Tooltip>

      <ChevronRightIcon sx={{ fontSize: 14, color: "#555", ml: 0.5, flexShrink: 0 }} />
    </Box>
  );
};

const SORT_OPTIONS = [
  { value: "severity", label: "Severity" },
  { value: "count",    label: "Count"    },
  { value: "score",    label: "Score"    },
];

// ── Main component ───────────────────────────────────────────────────────────
export default function AnomalyList({ anomalies = [], loading = false, onOpenDrawer, filterTypes }) {
  const [sortBy, setSortBy] = useState("severity");

  const grouped = useMemo(() => {
    const map = new Map();
    anomalies.forEach(a => {
      const src = a.entity?.id ?? "unknown";
      if (!map.has(src)) map.set(src, []);
      map.get(src).push(a);
    });

    const groups = Array.from(map.entries()).map(([ip, items]) => ({
      ip,
      items: [...items].sort((a, b) => (b.peak_score ?? 0) - (a.peak_score ?? 0)),
    }));

    if (sortBy === "severity") {
      return groups.sort((a, b) => {
        const sa = SEV_ORDER[worstSeverity(a.items)] ?? 99;
        const sb = SEV_ORDER[worstSeverity(b.items)] ?? 99;
        if (sa !== sb) return sa - sb;
        return b.items.length - a.items.length;
      });
    }
    if (sortBy === "count") {
      return groups.sort((a, b) => b.items.length - a.items.length);
    }
    // score
    return groups.sort((a, b) => (b.items[0].peak_score ?? 0) - (a.items[0].peak_score ?? 0));
  }, [anomalies, sortBy]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {loading && anomalies.length === 0 ? (
            <Skeleton variant="text" width={160} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
          ) : (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {anomalies.length} {anomalies.length === 1 ? "anomaly" : "anomalies"} — {grouped.length} {grouped.length === 1 ? "host" : "hosts"}
            </Typography>
          )}
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortBy}
          onChange={(_, v) => v && setSortBy(v)}
        >
          {SORT_OPTIONS.map(({ value, label }) => (
            <ToggleButton key={value} value={value} sx={{
              px: 1, py: 0.2, fontSize: 12, textTransform: "none",
              "&.Mui-selected": { bgcolor: "#45909522", color: "#459095", borderColor: "#459095" },
            }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {loading && anomalies.length === 0 ? (
        <Stack spacing={0.8}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={44} sx={{ bgcolor: "rgba(255,255,255,0.07)", borderRadius: "12px" }} />
          ))}
        </Stack>
      ) : !loading && grouped.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", border: "1px dashed #333", borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: 15, color: "text.secondary" }}>No anomalies found</Typography>
        </Box>
      ) : (
        grouped.map(({ ip, items }) => (
          <HostRow key={ip} ip={ip} items={items} onOpenDrawer={onOpenDrawer} filterTypes={filterTypes} />
        ))
      )}
    </Box>
  );
}
